import { useEffect, useState, useRef } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Edit3, Headphones, Search } from "lucide-react";

const EMPTY = {
  title: "", slug: "", category: "Tax Debt Solutions", price: 29, description: "",
  long_description: "", table_of_contents: "", cover_image_url: "", featured: false, published: true,
  has_audio: false, audio_price: 14.99, narrator: "", bundle_discount_pct: 25,
};

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function UploadProgress({ state }) {
  if (!state) return null;
  if (state.error) {
    return <div data-testid="upload-error" className="mt-1 text-[10px] text-red-600 max-w-[140px] leading-tight">{state.error}</div>;
  }
  return (
    <div className="mt-1" data-testid="upload-progress">
      <div className="w-28 h-1 bg-navy/10 rounded-full overflow-hidden">
        <div className="h-full bg-gold transition-all duration-200" style={{ width: `${state.pct}%` }} />
      </div>
      <div className="mt-0.5 text-[10px] text-navy/60">
        {state.done ? "✓ Uploaded" : `${state.pct}% · ${formatSize(state.size)}`}
      </div>
    </div>
  );
}

export default function AdminEbooks() {
  const [ebooks, setEbooks] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [progress, setProgress] = useState({});  // { "<ebookId>-<kind>": {pct, size, error} }
  const [limits, setLimits] = useState({ pdf: 100*1024*1024, preview: 25*1024*1024, cover: 10*1024*1024, audio: 500*1024*1024 });
  // Per-book toggle: when the admin uploads a new full audiobook, should we
  // auto-regenerate the 60-sec sample from its first minute? Default true =
  // current behavior. Set false when the new upload has an intro/outro and the
  // existing sample should be kept intact.
  const [regenAudioSample, setRegenAudioSample] = useState({}); // {[ebookId]: bool}
  const [viewLoading, setViewLoading] = useState({}); // {[ebookId-view-full|sample]: bool}
  const pdfRef = useRef({});
  const previewRef = useRef({});
  const coverRef = useRef({});
  const audioRef = useRef({});
  const audioSampleRef = useRef({});
  const chapterRef = useRef({});

  const load = () => api.get("/admin/ebooks").then((r) => setEbooks(r.data));
  useEffect(() => {
    load();
    api.get("/admin/upload-limits").then((r) => setLimits(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price),
      audio_price: form.audio_price !== "" && form.audio_price !== null ? parseFloat(form.audio_price) : null,
      bundle_discount_pct: form.bundle_discount_pct !== "" && form.bundle_discount_pct !== null ? parseFloat(form.bundle_discount_pct) : 25,
      table_of_contents: form.table_of_contents.split("\n").map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.patch(`/admin/ebooks/${editing}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/ebooks", payload);
        toast.success("Ebook created");
      }
      setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const edit = (b) => {
    setForm({ ...b, table_of_contents: (b.table_of_contents || []).join("\n") });
    setEditing(b.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!confirm("Delete this ebook?")) return;
    await api.delete(`/admin/ebooks/${id}`);
    toast.success("Deleted"); load();
  };

  const upload = async (id, file, kind, extraFields = {}) => {
    if (!file) return;
    const key = `${id}-${kind}`;
    const cap = limits[kind];
    if (cap && file.size > cap) {
      const limitMb = Math.round(cap / (1024 * 1024));
      const msg = `File is ${formatSize(file.size)} — exceeds the ${limitMb} MB limit for ${kind === "pdf" ? "full PDFs" : kind === "preview" ? "previews" : "cover images"}.`;
      setProgress((p) => ({ ...p, [key]: { pct: 0, size: file.size, error: msg } }));
      toast.error(msg);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    for (const [k, v] of Object.entries(extraFields)) {
      // FormData values must be strings/blobs — coerce bools cleanly.
      fd.append(k, typeof v === "boolean" ? String(v) : v);
    }
    setProgress((p) => ({ ...p, [key]: { pct: 0, size: file.size, error: null } }));
    try {
      await api.post(`/admin/ebooks/${id}/upload-${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // Audio files can be up to 500 MB + ffmpeg preview generation on the server takes time.
        timeout: kind === "audio" ? 600000 : 120000,
        onUploadProgress: (evt) => {
          const pct = evt.total ? Math.round((evt.loaded * 100) / evt.total) : 0;
          setProgress((p) => ({ ...p, [key]: { pct, size: file.size, error: null } }));
        },
      });
      setProgress((p) => ({ ...p, [key]: { pct: 100, size: file.size, error: null, done: true } }));
      const labels = { pdf: "PDF", preview: "Sample", cover: "Cover", audio: "Audiobook", chapter: "Chapter" };
      toast.success(`${labels[kind] || kind} uploaded (${formatSize(file.size)})`);
      setTimeout(() => setProgress((p) => { const q = { ...p }; delete q[key]; return q; }), 2000);
      load();
    } catch (e) {
      const msg = e.code === "ECONNABORTED"
        ? "Upload timed out after 120s. Try a smaller file or check your connection."
        : e.response?.status === 413
          ? (e.response?.data?.detail || "File too large for this upload slot.")
          : (e.response?.data?.detail || e.message || "Upload failed");
      setProgress((p) => ({ ...p, [key]: { pct: 0, size: file.size, error: msg } }));
      toast.error(`Upload failed: ${msg}`);
    }
  };

  // Admin PDF preview — fetches through the authenticated axios instance so the
  // bytes never travel over a public URL. Opens the resulting blob in a new tab
  // and revokes the object URL after 60 seconds so it can't be re-shared.
  const viewAdminPdf = async (id, slug, kind) => {
    const path = kind === "full" ? "pdf" : "sample-pdf";
    const label = kind === "full" ? "Full PDF" : "Sample";
    const key = `${id}-view-${kind}`;
    try {
      setViewLoading((s) => ({ ...s, [key]: true }));
      const res = await api.get(`/admin/ebooks/${id}/${path}`, { responseType: "blob" });
      // Force the blob's MIME type explicitly — some proxies strip Content-Type
      // and axios would then produce a generic `application/octet-stream` blob
      // that browsers won't render as a PDF.
      const pdfBlob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      const win = window.open(url, "_blank");
      if (!win) {
        toast.error("Popup blocked — allow popups for this site to preview PDFs.");
        URL.revokeObjectURL(url);
        return;
      }
      // Best-effort: give the browser time to load the blob before revoking.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.status === 404
        ? (kind === "full" ? "No PDF uploaded for this book yet." : "No sample generated — upload a PDF first.")
        : (e.message || "Preview failed");
      toast.error(`${label}: ${msg}`);
    } finally {
      setViewLoading((s) => { const q = { ...s }; delete q[key]; return q; });
    }
  };

  // Manual audio-sample upload — bypasses the auto first-minute slice. Reuses
  // the shared `upload()` helper by pointing it at a different endpoint suffix.
  const uploadAudioSample = async (id, file) => {
    if (!file) return;
    const key = `${id}-audio-sample`;
    setProgress((p) => ({ ...p, [key]: { pct: 0, size: file.size, error: null } }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/ebooks/${id}/upload-audio-sample`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
        onUploadProgress: (evt) => {
          const pct = evt.total ? Math.round((evt.loaded * 100) / evt.total) : 0;
          setProgress((p) => ({ ...p, [key]: { pct, size: file.size, error: null } }));
        },
      });
      setProgress((p) => ({ ...p, [key]: { pct: 100, size: file.size, error: null, done: true } }));
      toast.success(`Sample uploaded (${formatSize(file.size)})`);
      setTimeout(() => setProgress((p) => { const q = { ...p }; delete q[key]; return q; }), 2000);
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Sample upload failed";
      setProgress((p) => ({ ...p, [key]: { pct: 0, size: file.size, error: msg } }));
      toast.error(`Sample upload failed: ${msg}`);
    }
  };

  // Admin full-audio download — fetches through the auth'd axios (so the URL
  // is never public), turns the response into a blob, and triggers a browser
  // save-as. Useful for verifying two books actually contain different audio.
  const downloadAdminFullAudio = async (id, slug) => {
    const key = `${id}-audio-full-dl`;
    try {
      setViewLoading((s) => ({ ...s, [key]: true }));
      const res = await api.get(`/admin/ebooks/${id}/audio-full`, { responseType: "blob" });
      // Force the MIME type on the blob — if a proxy strips Content-Type in
      // transit, axios's fallback would be application/octet-stream and some
      // browsers save it as a generic .bin instead of a playable .mp3.
      const mp3Blob = new Blob([res.data], { type: "audio/mpeg" });
      const url = URL.createObjectURL(mp3Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      const msg = e.response?.status === 404
        ? "No full audio uploaded for this book yet."
        : (e.response?.data?.detail || e.message || "Download failed");
      toast.error(`Full MP3: ${msg}`);
    } finally {
      setViewLoading((s) => { const q = { ...s }; delete q[key]; return q; });
    }
  };

  return (
    <div data-testid="admin-ebooks-page">
      <div className="eyebrow mb-2">Catalog</div>
      <h1 className="aperture-h1 text-4xl mb-10">Ebooks.</h1>

      <form onSubmit={save} className="bg-white p-6 rounded-sm border mb-10 grid md:grid-cols-2 gap-4" style={{ borderColor: "rgba(10,37,64,0.10)" }} data-testid="admin-ebook-form">
        <div className="md:col-span-2 flex items-center justify-between">
          <div className="eyebrow">{editing ? "Edit ebook" : "Add new ebook"}</div>
          {editing && <button type="button" onClick={()=>{setForm(EMPTY); setEditing(null);}} className="text-xs text-navy/60 hover:text-gold">Cancel edit</button>}
        </div>
        <input required placeholder="Title" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value, slug: editing ? form.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"")})} data-testid="ebook-form-title" className="px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
        <input required placeholder="slug" value={form.slug} onChange={(e)=>setForm({...form, slug:e.target.value})} data-testid="ebook-form-slug" className="px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
        <select value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} data-testid="ebook-form-category" className="px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold bg-white" style={{ borderColor: "rgba(10,37,64,0.15)" }}>
          {["Tax Debt Solutions","Creative Financing","Foreclosure","Investor Playbooks"].map(c => <option key={c}>{c}</option>)}
        </select>
        <input required type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e)=>setForm({...form, price:e.target.value})} data-testid="ebook-form-price" className="px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
        <textarea required placeholder="Short description" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} data-testid="ebook-form-description" className="md:col-span-2 px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold min-h-20" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
        <textarea placeholder="Long description" value={form.long_description} onChange={(e)=>setForm({...form, long_description:e.target.value})} className="md:col-span-2 px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold min-h-20" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
        <textarea placeholder="Table of contents (one per line)" value={form.table_of_contents} onChange={(e)=>setForm({...form, table_of_contents:e.target.value})} className="md:col-span-2 px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold min-h-24" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
        <input placeholder="Cover image URL (or upload after creating)" value={form.cover_image_url} onChange={(e)=>setForm({...form, cover_image_url:e.target.value})} className="md:col-span-2 px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />

        {/* --- Audio companion fields --- */}
        <label className="md:col-span-2 flex items-center gap-2 text-sm border-t pt-4 mt-1" style={{ borderColor: "rgba(10,37,64,0.10)" }}>
          <input type="checkbox" checked={!!form.has_audio} onChange={(e)=>setForm({...form, has_audio:e.target.checked})} data-testid="ebook-form-has-audio" />
          <Headphones className="w-4 h-4 text-gold" strokeWidth={1.5}/>
          <span className="font-medium">This ebook has an audio version</span>
        </label>
        {form.has_audio && (
          <>
            <input placeholder="Narrator name (e.g., Jane Doe)" value={form.narrator || ""} onChange={(e)=>setForm({...form, narrator:e.target.value})} data-testid="ebook-form-narrator" className="px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            <input type="number" step="0.01" placeholder="Audio price (USD)" value={form.audio_price ?? ""} onChange={(e)=>setForm({...form, audio_price:e.target.value})} data-testid="ebook-form-audio-price" className="px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            <div className="md:col-span-2 grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-navy/60 block mb-1">Bundle discount % <span className="text-navy/40">(default 25)</span></label>
                <input type="number" step="1" min="0" max="90" value={form.bundle_discount_pct ?? 25} onChange={(e)=>setForm({...form, bundle_discount_pct:e.target.value})} data-testid="ebook-form-bundle-pct" className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
              </div>
              <div className="text-xs text-navy/60 flex items-end pb-1">
                <div>
                  Bundle price preview:{" "}
                  <span className="text-navy font-medium">
                    ${(() => {
                      const p = parseFloat(form.price) || 0;
                      const a = parseFloat(form.audio_price) || 0;
                      const pct = parseFloat(form.bundle_discount_pct ?? 25) || 0;
                      return ((p + a) * (1 - pct / 100)).toFixed(2);
                    })()}
                  </span>{" "}
                  <span className="text-navy/40">(vs ${((parseFloat(form.price) || 0) + (parseFloat(form.audio_price) || 0)).toFixed(2)} full)</span>
                </div>
              </div>
            </div>
          </>
        )}
        {/* --- /Audio companion fields --- */}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e)=>setForm({...form, published:e.target.checked})} /> Published</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e)=>setForm({...form, featured:e.target.checked})} /> Featured</label>
        <div className="md:col-span-2"><button data-testid="ebook-form-submit" className="btn-primary px-6 py-3 text-xs uppercase tracking-widest inline-flex gap-2 rounded-sm"><Plus className="w-4 h-4" /> {editing ? "Save Changes" : "Create Ebook"}</button></div>
      </form>

      <div className="bg-white rounded-sm border overflow-x-auto" style={{ borderColor: "rgba(10,37,64,0.10)" }}>
        <div className="px-4 py-2.5 border-b text-[11px] text-navy/50 uppercase tracking-widest" style={{ borderColor: "rgba(10,37,64,0.10)" }}>
          Upload limits — PDF: {Math.round(limits.pdf/(1024*1024))} MB · Preview: {Math.round(limits.preview/(1024*1024))} MB · Cover: {Math.round(limits.cover/(1024*1024))} MB · Audio: {Math.round((limits.audio || 500*1024*1024)/(1024*1024))} MB
        </div>
        <table className="w-full text-sm">
          <thead className="bg-navy/5">
            <tr className="text-left">{["Cover","Title","Category","Price","PDF","Sample (auto)","Chapter (email)","Audio","Actions"].map(h => <th key={h} className="px-4 py-3 eyebrow" style={{ color: "#0A2540" }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {ebooks.map((b) => (
              <tr key={b.id} className="border-t" data-testid={`ebook-row-${b.slug}`} style={{ borderColor: "rgba(10,37,64,0.08)" }}>
                <td className="px-4 py-3">
                  <div className="w-10 h-14 bg-[#F7F5F0] rounded-sm flex items-center justify-center p-0.5">
                    <img src={b.cover_image_url} alt="" className="max-w-full max-h-full w-auto h-auto object-contain" />
                  </div>
                </td>
                <td className="px-4 py-3 font-medium">{b.title}<div className="text-xs text-navy/50">{b.slug}</div></td>
                <td className="px-4 py-3 text-navy/70">{b.category}</td>
                <td className="px-4 py-3">${b.price.toFixed(2)}</td>
                <td className="px-4 py-3">
                  {b.pdf_storage_path ? <span className="text-gold text-xs">✓ Uploaded</span> : <span className="text-navy/40 text-xs">—</span>}
                  <div className="mt-1 flex items-center gap-2">
                    <input type="file" accept="application/pdf" ref={(el)=>pdfRef.current[b.id]=el} onChange={(e)=>upload(b.id, e.target.files[0], "pdf")} className="hidden" data-testid={`ebook-pdf-input-${b.slug}`} />
                    <button onClick={()=>pdfRef.current[b.id]?.click()} data-testid={`ebook-pdf-upload-${b.slug}`} className="inline-flex items-center gap-1 text-xs text-navy hover:text-gold"><Upload className="w-3 h-3"/>Upload</button>
                    {b.pdf_storage_path && (
                      <button
                        onClick={()=>viewAdminPdf(b.id, b.slug, "full")}
                        disabled={!!viewLoading[`${b.id}-view-full`]}
                        title="Preview the full PDF in a new tab"
                        data-testid={`ebook-pdf-view-${b.slug}`}
                        className="inline-flex items-center text-navy/70 hover:text-gold disabled:opacity-40 disabled:cursor-not-allowed"
                      ><Search className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                  <UploadProgress state={progress[`${b.id}-pdf`]} />
                </td>
                <td className="px-4 py-3">
                  {b.preview_storage_path ? <span className="text-gold text-xs">✓ 6-page sample</span> : <span className="text-navy/40 text-xs">— auto on next PDF upload</span>}
                  <div className="mt-1 flex items-center gap-2">
                    <input type="file" accept="application/pdf" ref={(el)=>previewRef.current[b.id]=el} onChange={(e)=>upload(b.id, e.target.files[0], "preview")} className="hidden" data-testid={`ebook-preview-input-${b.slug}`} />
                    <button onClick={()=>previewRef.current[b.id]?.click()} className="inline-flex items-center gap-1 text-xs text-navy hover:text-gold"><Upload className="w-3 h-3"/>Override</button>
                    {b.preview_storage_path && (
                      <button
                        onClick={()=>viewAdminPdf(b.id, b.slug, "sample")}
                        disabled={!!viewLoading[`${b.id}-view-sample`]}
                        title="Preview the 6-page sample in a new tab"
                        data-testid={`ebook-sample-view-${b.slug}`}
                        className="inline-flex items-center text-navy/70 hover:text-gold disabled:opacity-40 disabled:cursor-not-allowed"
                      ><Search className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                  <UploadProgress state={progress[`${b.id}-preview`]} />
                </td>
                <td className="px-4 py-3">
                  {b.chapter_storage_path ? <span className="text-gold text-xs">✓</span> : <span className="text-navy/40 text-xs">—</span>}
                  <div><input type="file" accept="application/pdf" ref={(el)=>chapterRef.current[b.id]=el} onChange={(e)=>upload(b.id, e.target.files[0], "chapter")} className="hidden" data-testid={`ebook-chapter-input-${b.slug}`} />
                    <button onClick={()=>chapterRef.current[b.id]?.click()} data-testid={`ebook-chapter-upload-${b.slug}`} className="mt-1 inline-flex items-center gap-1 text-xs text-navy hover:text-gold"><Upload className="w-3 h-3"/>Chapter</button>
                  </div>
                  <UploadProgress state={progress[`${b.id}-chapter`]} />
                </td>
                <td className="px-4 py-3">
                  {b.has_audio ? (
                    <>
                      {b.audio_storage_path
                        ? <span className="text-gold text-xs">✓ {b.audio_duration_sec ? `${Math.floor(b.audio_duration_sec/60)}m ${b.audio_duration_sec%60}s` : "Uploaded"}</span>
                        : <span className="text-navy/40 text-xs">—</span>}
                      {b.audio_preview_storage_path && (
                        <div className="mt-1" title="60-second sample that customers hear after email capture">
                          <div className="text-[10px] text-navy/50 uppercase tracking-wider mb-0.5">Sample</div>
                          <audio
                            controls
                            preload="none"
                            src={`${API}/media/${b.audio_preview_storage_path}`}
                            className="w-40 h-7"
                            data-testid={`ebook-audio-sample-player-${b.slug}`}
                          />
                        </div>
                      )}
                      <div className="mt-1">
                        <input type="file" accept="audio/mpeg,audio/mp3,.mp3" ref={(el)=>audioSampleRef.current[b.id]=el}
                          onChange={(e)=>uploadAudioSample(b.id, e.target.files[0])}
                          className="hidden" data-testid={`ebook-audio-sample-input-${b.slug}`} />
                        <button onClick={()=>audioSampleRef.current[b.id]?.click()}
                          title="Upload a hand-picked 60-sec sample MP3 (replaces the current sample; full audio untouched)"
                          data-testid={`ebook-audio-sample-upload-${b.slug}`}
                          className="inline-flex items-center gap-1 text-[11px] text-navy/70 hover:text-gold">
                          <Upload className="w-3 h-3"/>Upload Sample
                        </button>
                      </div>
                      <UploadProgress state={progress[`${b.id}-audio-sample`]} />
                      <label className="mt-1 flex items-start gap-1.5 text-[10px] text-navy/70 leading-tight cursor-pointer max-w-[160px]" title="When checked (default), the new upload's first minute becomes the 60-sec sample and overwrites any existing sample. Uncheck if your new upload has an intro/outro — the full audio will be replaced but the existing sample stays untouched.">
                        <input
                          type="checkbox"
                          checked={regenAudioSample[b.id] ?? true}
                          onChange={(e) => setRegenAudioSample((s) => ({ ...s, [b.id]: e.target.checked }))}
                          data-testid={`ebook-audio-regen-sample-${b.slug}`}
                          className="mt-0.5"
                        />
                        <span>Regenerate 60-sec sample from this upload</span>
                      </label>
                      <div className="mt-1 flex items-center gap-3 flex-wrap">
                        <input type="file" accept="audio/mpeg,audio/mp3,.mp3" ref={(el)=>audioRef.current[b.id]=el}
                          onChange={(e)=>upload(b.id, e.target.files[0], "audio", { regenerate_preview: regenAudioSample[b.id] ?? true })}
                          className="hidden" data-testid={`ebook-audio-input-${b.slug}`} />
                        <button onClick={()=>audioRef.current[b.id]?.click()} data-testid={`ebook-audio-upload-${b.slug}`} className="inline-flex items-center gap-1 text-xs text-navy hover:text-gold"><Headphones className="w-3 h-3"/>MP3</button>
                        {b.audio_storage_path && (
                          <button onClick={()=>downloadAdminFullAudio(b.id, b.slug)}
                            disabled={!!viewLoading[`${b.id}-audio-full-dl`]}
                            title="Download the full audiobook MP3 to verify content"
                            data-testid={`ebook-audio-full-download-${b.slug}`}
                            className="inline-flex items-center gap-1 text-[11px] text-navy/70 hover:text-gold disabled:opacity-40 disabled:cursor-not-allowed">
                            <Search className="w-3 h-3"/>Full MP3
                          </button>
                        )}
                      </div>
                      <UploadProgress state={progress[`${b.id}-audio`]} />
                    </>
                  ) : (
                    <span className="text-navy/30 text-xs italic">Audio off</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <input type="file" accept="image/*" ref={(el)=>coverRef.current[b.id]=el} onChange={(e)=>upload(b.id, e.target.files[0], "cover")} className="hidden" />
                  <button onClick={()=>coverRef.current[b.id]?.click()} className="inline-flex items-center gap-1 text-xs text-navy hover:text-gold mr-3"><Upload className="w-3 h-3"/>Cover</button>
                  <UploadProgress state={progress[`${b.id}-cover`]} />
                  <button onClick={()=>edit(b)} className="inline-flex items-center gap-1 text-xs text-navy hover:text-gold mr-3" data-testid={`ebook-edit-${b.slug}`}><Edit3 className="w-3 h-3"/>Edit</button>
                  <button onClick={()=>del(b.id)} data-testid={`ebook-delete-${b.slug}`} className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"><Trash2 className="w-3 h-3"/>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
